"use client";

import React, { useRef, useEffect } from "react";
import AsteroideGray from "../AsteroideGray/AsteroideGray";
import Start3V2WithGlobeYellowSingleMedium from "../Start3V2WithGlobeYellowSingleMedium/Start3V2WithGlobeYellowSingleMedium";
import GlobeYellowSingleLittle from "../GlobeYellowSingleLittle/GlobeYellowSingleLittle";
import GlobeYellowSingleBig from "../GlobeYellowSingleBig/GlobeYellowSingleBig";
import { _LandingHighlighProductPetitPrinceDecorationBaseContainer } from "./LandingHighlighProductPetitPrinceDecorationBase.styled";
import { IStyledDivStyles } from "../../../../../StyledDiv/StyledDiv";
import StyledDiv from "../../../../../StyledDiv/StyledDiv";
import useBreakpoint from "../../../../../Grid/hooks/useBreakpoint";
import { lazyGsap } from "../../../../../../lib/gsap/lazyGsap";

interface ILandingHighlighProductPetitPrinceDecorationBaseProps {
  styles?: IStyledDivStyles;
  /** Référence à l'élément trigger pour le scroll */
  triggerRef?: React.RefObject<HTMLElement>;
  /** Activer l'animation de parallax au scroll */
  enableScrollAnimation?: boolean;
}

// Vitesses de parallax pour chaque décoration (multipliées par la hauteur du viewport)
// 1.0 = suit exactement le scroll, >1 = monte plus vite que le scroll
// { desktop, mobile } - valeurs différentes selon le device
const PARALLAX_SPEEDS = {
  asteroideGray: { desktop: 0.3, mobile: 0.25 },
  start3V2WithGlobe: { desktop: 0.5, mobile: 0.4 },
  globeYellowLittle: { desktop: 0.4, mobile: 0.35 },
  globeYellowBig: { desktop: 0.6, mobile: 0.5 },
};

const LandingHighlighProductPetitPrinceDecorationBase = ({
  styles,
  triggerRef,
  enableScrollAnimation = true,
}: ILandingHighlighProductPetitPrinceDecorationBaseProps) => {
  const { isMobile } = useBreakpoint();
  const containerRef = useRef<HTMLDivElement>(null);
  const asteroideGrayRef = useRef<HTMLDivElement>(null);
  const start3V2WithGlobeRef = useRef<HTMLDivElement>(null);
  const globeYellowLittleRef = useRef<HTMLDivElement>(null);
  const globeYellowBigRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !enableScrollAnimation) return;

    const trigger = triggerRef?.current || containerRef.current;
    if (!trigger) return;

    let cancelled = false;
    let cleanup: (() => void) | undefined;

    lazyGsap().then(({ gsap, ScrollTrigger }) => {
      if (cancelled) return;

    // Sur mobile : animation de parallax simple sur le container uniquement
    if (isMobile) {
      const viewportHeight = window.innerHeight;
      const parallaxSpeed = 0.5; // Vitesse de parallax pour le container

      // Réinitialise les transforms GSAP avant de recréer l'animation
      if (containerRef.current) {
        gsap.set(containerRef.current, { clearProps: "y,transform" });
      }

      // QuickSetter pour le container
      const containerSetter = containerRef.current
        ? gsap.quickSetter(containerRef.current, "y", "px")
        : null;
      const maxDistance = -viewportHeight * parallaxSpeed;

      // Variable pour stocker la progression actuelle
      let currentProgress = 0;
      let targetProgress = 0;
      let rafId: number | null = null;

      // Facteur de lissage pour mobile
      const smoothFactor = 0.08;

      // Fonction d'animation avec lerp pour un mouvement fluide
      const animate = () => {
        // Interpolation linéaire pour lisser le mouvement
        currentProgress += (targetProgress - currentProgress) * smoothFactor;

        // Appliquer la transformation au container
        if (containerSetter) {
          containerSetter(currentProgress * maxDistance);
        }

        // Continuer l'animation si on n'a pas atteint la cible
        if (Math.abs(targetProgress - currentProgress) > 0.0001) {
          rafId = requestAnimationFrame(animate);
        } else {
          rafId = null;
        }
      };

      // ScrollTrigger pour le parallax du container
      const scrollTrigger = ScrollTrigger.create({
        trigger: trigger,
        start: "top 84px",
        end: "bottom top",
        onUpdate: (self) => {
          targetProgress = self.progress;

          // Démarrer l'animation si elle n'est pas déjà en cours
          if (rafId === null) {
            rafId = requestAnimationFrame(animate);
          }
        },
        fastScrollEnd: true,
        preventOverlaps: true,
      });

      // Force un refresh de ScrollTrigger après la création
      ScrollTrigger.refresh();

      cleanup = () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        scrollTrigger.kill();
        if (containerRef.current) {
          gsap.set(containerRef.current, { clearProps: "y,transform" });
        }
      };
      return;
    }

    // Sur desktop : animation des décorations individuelles (code original)
    if (!isMobile) {
    const getSpeed = (speeds: { desktop: number; mobile: number }) =>
      speeds.desktop;

    const decorations = [
      { ref: asteroideGrayRef, speed: getSpeed(PARALLAX_SPEEDS.asteroideGray) },
      {
        ref: start3V2WithGlobeRef,
        speed: getSpeed(PARALLAX_SPEEDS.start3V2WithGlobe),
      },
      {
        ref: globeYellowLittleRef,
        speed: getSpeed(PARALLAX_SPEEDS.globeYellowLittle),
      },
      {
        ref: globeYellowBigRef,
        speed: getSpeed(PARALLAX_SPEEDS.globeYellowBig),
      },
    ];

    const animations: any[] = [];

    // Réinitialise les transforms GSAP avant de recréer les animations
    decorations.forEach(({ ref }) => {
      if (ref.current) {
        gsap.set(ref.current, { clearProps: "y" });
      }
    });

    decorations.forEach(({ ref, speed }) => {
      if (!ref.current) return;

      // Calcul du déplacement basé sur la hauteur du viewport
      const viewportHeight = window.innerHeight;
      const moveDistance = -viewportHeight * speed;

      const tween = gsap.to(ref.current, {
        y: moveDistance,
        ease: "none",
        immediateRender: false, // Empêche l'application des valeurs au chargement
        scrollTrigger: {
          trigger: trigger,
          start: "top top",
          end: "bottom top",
          scrub: 0.5,
          invalidateOnRefresh: true, // Recalcule les valeurs lors du resize
        },
      });

      animations.push(tween);
    });

      // Force un refresh de ScrollTrigger après la création des animations
      ScrollTrigger.refresh();

      cleanup = () => {
        animations.forEach((tween) => {
          tween.scrollTrigger?.kill();
          tween.kill();
        });
        decorations.forEach(({ ref }) => {
          if (ref.current) {
            gsap.set(ref.current, { clearProps: "y" });
          }
        });
      };
    }
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [enableScrollAnimation, triggerRef, isMobile]);

  return (
    <StyledDiv ref={containerRef} styles={styles}>
      <_LandingHighlighProductPetitPrinceDecorationBaseContainer>
        {/* AsteroideGray decoration */}
        <StyledDiv
          ref={asteroideGrayRef}
          styles={{
            position: "relative",
            zIndex: 1,
            willChange: "transform",
          }}
        >
          <AsteroideGray />
        </StyledDiv>

        {/* Start3V2WithGlobeYellowSingleMedium decoration */}
        <StyledDiv
          ref={start3V2WithGlobeRef}
          styles={{
            position: "absolute",
            zIndex: 1,
            top: "10%",
            left: "24%",
            willChange: "transform",
            mobile: {
              display: "none",
            },
          }}
        >
          <Start3V2WithGlobeYellowSingleMedium />
        </StyledDiv>

        {/* GlobeYellowSingleLittle decoration */}
        <StyledDiv
          ref={globeYellowLittleRef}
          styles={{
            position: "absolute",
            zIndex: 1,
            top: "22%",
            left: "12%",
            willChange: "transform",
            mobile: {
              top: "24%",
              left: "47%",
            },
          }}
        >
          <GlobeYellowSingleLittle />
        </StyledDiv>

        {/* GlobeYellowSingleBig decoration */}
        <StyledDiv
          ref={globeYellowBigRef}
          styles={{
            position: "absolute",
            zIndex: 1,
            top: "39%",
            left: "4%",
            willChange: "transform",
          }}
        >
          <GlobeYellowSingleBig />
        </StyledDiv>
      </_LandingHighlighProductPetitPrinceDecorationBaseContainer>
    </StyledDiv>
  );
};

export default LandingHighlighProductPetitPrinceDecorationBase;
